import { Col, Row, Tabs } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AdminSearch from "../../components/AdminSearch/AdminSearch";
import permissionsUtil from "../../utils/permissions.util";
import { selectIsFeatureFlagPairEnabled } from "../../store/selectors/featureFlag.selector";
import { getFeatureFlags } from "../../utils/utils";
import Brokers from "../Brokers/Brokers";
import EmployeeContacts from "../EmployeeContacts/EmployeeContacts";
import FeatureFlag from "../FeatureFlag/FeatureFlag";
import HelperText from "../HelperText/HelperText";
import Import from "../Import/Import";
import Markets from "../Markets/Markets";
import MediaAssets from "../MediaAssets/MediaAssets";
import PropertyContactsHistory from "../PropertyContactsHistory/PropertyContactsHistory";
import PropertyRollUp from "../PropertyRollUp/PropertyRollUp";
import RFMChanges from "../RFMChanges/RFMChanges";
import Template from "../Template/Template";
import UnmarketReasons from "../UnmarketReasons/UnmarketReasons";
import "./Dashboard.scss";
const { TabPane } = Tabs;

export const filterAuthorizedAdminTabs = (tabs, checkAuth = permission => permissionsUtil.checkAuth(permission)) =>
  tabs.filter(tab => {
    const requiredPermissions = Array.isArray(tab.permission) ? tab.permission : [tab.permission];
    return tab.enabled !== false && requiredPermissions.some(checkAuth);
  });

export const resolveAuthorizedAdminTab = (authorizedTabs, hash) =>
  authorizedTabs.find(tab => tab.hash === hash) || authorizedTabs[0];

const currentHash = () => window.location.hash.replace(/^#/, "");

export const DashboardContainer = () => {
  const { t } = useTranslation();
  const isImportEnabled = useSelector(state =>
    selectIsFeatureFlagPairEnabled(state, "DRY_RUN_IMPORT", "DRY_RUN_IMPORT_TAB"),
  );
  const featureFlags = getFeatureFlags();
  const isRFMEnabled = Boolean(featureFlags?.USE_READY_FOR_MARKETING_FLAG?.status);
  const isMediaAssetsEnabled = Boolean(featureFlags?.property_search_redesign?.status);
  const tabs = useMemo(
    () => [
      {
        key: "1",
        hash: "employeeContacts",
        label: t("DashboardContainerTabs.EmployeeContacts"),
        permission: "user_view",
        content: <EmployeeContacts />,
        search: true,
      },
      {
        key: "2",
        hash: "brokers",
        label: t("DashboardContainerTabs.Brokers"),
        permission: "user_view",
        content: <Brokers />,
        search: true,
      },
      {
        key: "4",
        hash: "markets",
        label: t("DashboardContainerTabs.Markets"),
        permission: "market_view",
        content: <Markets />,
        search: true,
      },
      {
        key: "5",
        hash: "propertyRollUp",
        label: t("DashboardContainerTabs.PropertyRollUp"),
        permission: "property_roll_up_view",
        content: <PropertyRollUp />,
        search: true,
      },
      {
        key: "6",
        hash: "contactsHistory",
        label: t("DashboardContainerTabs.ContactsChangeHistory"),
        permission: "property_contact_history_export",
        content: <PropertyContactsHistory />,
        search: true,
      },
      {
        key: "11",
        hash: "rfmChanges",
        label: t("DashboardContainerTabs.RFMChanges"),
        permission: "rfm_changes",
        content: <RFMChanges />,
        search: true,
        enabled: isRFMEnabled,
      },
      {
        key: "7",
        hash: "helperText",
        label: t("DashboardContainerTabs.HelperText"),
        permission: "helper_text_view",
        content: <HelperText />,
      },
      {
        key: "8",
        hash: "templates",
        label: t("DashboardContainerTabs.Templates"),
        permission: "template_view",
        content: <Template />,
      },
      {
        key: "9",
        hash: "featureFlag",
        label: t("DashboardContainerTabs.FeatureFlag"),
        permission: "feature_flag_view",
        content: <FeatureFlag />,
      },
      {
        key: "10",
        hash: "unmarketReason",
        label: t("DashboardContainerTabs.UnmarketReason"),
        permission: "unmarket_reason_view",
        content: <UnmarketReasons />,
      },
      {
        key: "12",
        hash: "mediaAssets",
        label: t("DashboardContainerTabs.MediaAssets"),
        permission: ["user_view", "market_view", "feature_flag_view"],
        content: <MediaAssets />,
        enabled: isMediaAssetsEnabled,
      },
      {
        key: "13",
        hash: "import",
        label: t("DashboardContainerTabs.Import"),
        permission: "import_view",
        content: <Import />,
        enabled: isImportEnabled,
      },
    ],
    [isImportEnabled, isMediaAssetsEnabled, isRFMEnabled, t],
  );
  const authorizedTabs = useMemo(() => filterAuthorizedAdminTabs(tabs), [tabs]);
  const initialTab = resolveAuthorizedAdminTab(authorizedTabs, currentHash());
  const [activeKey, setActiveKey] = useState(initialTab && initialTab.key);
  const [type, setType] = useState(initialTab && initialTab.hash);
  const authorizationSignature = authorizedTabs.map(tab => tab.hash).join("|");

  useEffect(() => {
    const selectHash = () => {
      const selected = resolveAuthorizedAdminTab(authorizedTabs, currentHash());
      if (!selected) return;
      setActiveKey(selected.key);
      setType(selected.hash);
      if (currentHash() !== selected.hash) window.location.hash = selected.hash;
    };
    selectHash();
    window.addEventListener("hashchange", selectHash);
    return () => window.removeEventListener("hashchange", selectHash);
  }, [authorizationSignature, authorizedTabs]);

  const onTabChange = key => {
    const selected = authorizedTabs.find(tab => tab.key === key);
    if (!selected) return;
    setActiveKey(selected.key);
    setType(selected.hash);
    window.location.hash = selected.hash;
  };
  const activeTab = authorizedTabs.find(tab => tab.key === activeKey) || authorizedTabs[0];
  const tabsKey = authorizedTabs.reduce((result, tab) => ({ ...result, [tab.key]: tab.hash }), {});

  return (
    <div className="dashboard">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {activeTab && activeTab.search && <AdminSearch tabsKey={tabsKey} type={type} />}
          <Tabs className="tabs__container" onChange={onTabChange} activeKey={activeTab && activeTab.key}>
            {authorizedTabs.map(tab => (
              <TabPane className="tabs__container-item" tab={tab.label} key={tab.key}>
                {tab.content}
              </TabPane>
            ))}
          </Tabs>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardContainer;
